import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useAppSelector';
import AutoSuggestInput from './AutoSuggestInput';
import AlertMessage from '../../components/AlertMessage';

const MenuSection = ({ menuItems, allMenuItems, addMenuItem, updateMenuItem, deleteMenuItem, toggleAvailability, handleToggelQuickServe, isFullscreen }) => {
    const navigate = useNavigate();
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;

    const [newItem, setNewItem] = useState({
        itemName: '',
        itemPrice: '',
        itemType: 'veg',
        itemCategory: 'meal',
        itemImage: '',
        itemDescription: '',
        restaurantId: '',
        suggestedItems: [] // will store an array of selected suggestion objects
    });

    const [editingItem, setEditingItem] = useState(null);
    const [initialSuggestedItem, setInitialSuggestedItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const fileInputRef = useRef(null);

    // Filter and search menu items
    const filteredMenuItems = useMemo(() => {
        return menuItems.filter(item => {
            const matchesSearch = item.itemName.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = filterCategory === 'all' || item.itemCategory === filterCategory;
            const matchesType = filterType === 'all' || item.itemType === filterType;
            return matchesSearch && matchesCategory && matchesType;
        });
    }, [menuItems, searchQuery, filterCategory, filterType]);

    const handleChange = (e) => {
        setNewItem({ ...newItem, [e.target.name]: e.target.value });
        // Clear messages when user starts typing
        if (errorMessage) setErrorMessage('');
        if (successMessage) setSuccessMessage('');
    };

    // Image compression utility
    const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Calculate new dimensions
                    if (width > height) {
                        if (width > maxWidth) {
                            height = Math.round((height * maxWidth) / width);
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxHeight) {
                            width = Math.round((width * maxHeight) / height);
                            height = maxHeight;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Convert to base64 with compression
                    const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                    resolve(compressedBase64);
                };
                img.onerror = reject;
            };
            reader.onerror = reject;
        });
    };

    // Handle image upload from gallery
    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setErrorMessage('⚠️ Please select a valid image file');
            return;
        }

        // Validate file size (max 5MB before compression)
        if (file.size > 5 * 1024 * 1024) {
            setErrorMessage('⚠️ Image size should be less than 5MB');
            return;
        }

        try {
            setUploadingImage(true);
            setErrorMessage('');
            setSuccessMessage('');

            // Compress the image
            const compressedImage = await compressImage(file);
            
            // Verify the compressed image is valid
            if (!compressedImage || !compressedImage.startsWith('data:image')) {
                throw new Error('Failed to compress image properly');
            }
            
            // Log image size for debugging
            console.log('Compressed image size:', compressedImage.length, 'characters');
            
            // Update the itemImage field with compressed base64
            setNewItem({ ...newItem, itemImage: compressedImage });
            setSuccessMessage('✅ Image uploaded and compressed successfully!');
            
            // Clear the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Error compressing image:', error);
            setErrorMessage('⚠️ Failed to process image. Please try again.');
        } finally {
            setUploadingImage(false);
        }
    };

    // Handle image deletion
    const handleDeleteImage = () => {
        setNewItem({ ...newItem, itemImage: '' });
        // Clear the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setSuccessMessage('✅ Image removed successfully');
        // Clear success message after 2 seconds
        setTimeout(() => {
            setSuccessMessage('');
        }, 2000);
    };

    // Callback from AutoSuggestInput returns an array of selected suggestion objects
    const handleSuggestedItemsChange = (selected) => {
        setNewItem({ ...newItem, suggestedItems: selected });
    };

    const handleAddItem = async () => {
        // Validation
        if (!newItem.itemName.trim()) {
            setErrorMessage('⚠️ Item name is required');
            return;
        }
        if (!newItem.itemPrice || parseFloat(newItem.itemPrice) <= 0) {
            setErrorMessage('⚠️ Valid price is required');
            return;
        }
        if (!newItem.itemImage.trim()) {
            setErrorMessage('⚠️ Image is required');
            return;
        }

        try {
            setErrorMessage('');
            setSuccessMessage('');
            
            // Convert the selected suggestion objects to an array of IDs
            const itemToAdd = {
                ...newItem,
                restaurantId: restaurantId,
                suggestedItems: newItem.suggestedItems.map(item => item._id)
            };
            
            const savedItem = await addMenuItem(itemToAdd);
            
            // Verify the image was saved correctly
            if (savedItem && savedItem.itemImage) {
                console.log('Menu item saved with image. Image length:', savedItem.itemImage.length);
                setSuccessMessage('✅ Menu item added successfully!');
            } else {
                console.error('Menu item saved but image is missing:', savedItem);
                setErrorMessage('⚠️ Menu item added but image may not have been saved correctly. Please check.');
            }
            
            setNewItem({
                itemName: '',
                itemPrice: '',
                itemType: 'veg',
                itemCategory: 'meal',
                itemImage: '',
                itemDescription: '',
                restaurantId: restaurantId,
                suggestedItems: []
            });
            setInitialSuggestedItem([]);
            
            // Clear messages after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
                setShowForm(false);
            }, 3000);
        } catch (error) {
            const errorMsg = error?.message || error?.response?.data?.message || 'Failed to add menu item. Please try again.';
            setErrorMessage(`⚠️ ${errorMsg}`);
            console.error('Error adding menu item:', error);
        }
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        // Clear messages when editing
        setErrorMessage('');
        setSuccessMessage('');
        // Convert the suggestedItems (which is an array of IDs) into objects from allMenuItems
        const selected = allMenuItems.filter(mi => item.suggestedItems.includes(mi._id.toString()));
        setNewItem({
            itemName: item.itemName,
            itemPrice: item.itemPrice,
            itemType: item.itemType,
            itemCategory: item.itemCategory,
            itemImage: item.itemImage,
            itemDescription: item.itemDescription || '',
            restaurantId: item.restaurantId,
            suggestedItems: selected
        });
        setInitialSuggestedItem(item.suggestedItems);
    };

    const handleUpdateItem = async () => {
        if (!editingItem) return;

        // Validation
        if (!newItem.itemName.trim()) {
            setErrorMessage('⚠️ Item name is required');
            return;
        }
        if (!newItem.itemPrice || parseFloat(newItem.itemPrice) <= 0) {
            setErrorMessage('⚠️ Valid price is required');
            return;
        }
        if (!newItem.itemImage.trim()) {
            setErrorMessage('⚠️ Image is required');
            return;
        }

        try {
            setErrorMessage('');
            setSuccessMessage('');
            
            const updatedItem = {
                ...newItem,
                suggestedItems: newItem.suggestedItems.map(item => item._id)
            };
            
            const updatedItemResult = await updateMenuItem(editingItem._id, updatedItem);
            
            // Verify the image was saved correctly
            if (updatedItemResult && updatedItemResult.itemImage) {
                console.log('Menu item updated with image. Image length:', updatedItemResult.itemImage.length);
                setSuccessMessage('✅ Menu item updated successfully!');
            } else {
                console.error('Menu item updated but image is missing:', updatedItemResult);
                setErrorMessage('⚠️ Menu item updated but image may not have been saved correctly. Please check.');
            }
            
            setNewItem({
                itemName: '',
                itemPrice: '',
                itemType: 'veg',
                itemCategory: 'meal',
                itemImage: '',
                itemDescription: '',
                restaurantId: restaurantId,
                suggestedItems: []
            });
            setEditingItem(null);
            setInitialSuggestedItem([]);
            
            // Clear messages after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
                setShowForm(false);
            }, 3000);
        } catch (error) {
            const errorMsg = error?.message || error?.response?.data?.message || 'Failed to update menu item. Please try again.';
            setErrorMessage(`⚠️ ${errorMsg}`);
            console.error('Error updating menu item:', error);
        }
    };

    const handleToggleAvailability = (id) => {
        toggleAvailability(id);
    };

    const emptyNewItem = () => ({
        itemName: '',
        itemPrice: '',
        itemType: 'veg',
        itemCategory: 'meal',
        itemImage: '',
        itemDescription: '',
        restaurantId: restaurantId || '',
        suggestedItems: []
    });

    const closeMenuForm = () => {
        setShowForm(false);
        setEditingItem(null);
        setInitialSuggestedItem([]);
        setNewItem(emptyNewItem());
        setErrorMessage('');
        setSuccessMessage('');
    };

    const formOpen = showForm || editingItem;

    const categoryOptions = [
        { value: 'all', label: 'All Menu', icon: '🍽️' },
        { value: 'starters', label: 'Starters', icon: '🍢' },
        { value: 'mainCourse', label: 'Main Course', icon: '🍽️' },
        { value: 'biryani', label: 'Biryani', icon: '🍛' },
        { value: 'rice', label: 'Rice', icon: '🍚' },
        { value: 'curries', label: 'Curries', icon: '🍲' },
        { value: 'breads', label: 'Breads', icon: '🍞' },
        { value: 'dessert', label: 'Dessert', icon: '🍰' },
        { value: 'beverages', label: 'Beverages', icon: '🥤' },
        { value: 'todaysSpecial', label: "Today's Special", icon: '⭐' },
        { value: 'breakfast', label: 'Breakfast', icon: '🍳' },
        { value: 'lunch', label: 'Lunch', icon: '🍱' },
        { value: 'meal', label: 'Meal', icon: '🍽️' },
        { value: 'soups', label: 'Soups', icon: '🍜' },
        { value: 'tandoor', label: 'Tandoor', icon: '🔥' },
        { value: 'chinese', label: 'Chinese', icon: '🥡' },
    ];

    const getCategoryCount = (value) => {
        if (value === 'all') return menuItems.length;
        return menuItems.filter((i) => i.itemCategory === value).length;
    };

    const DeleteIcon = () => (
        <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
    );
    const EditIcon = () => (
        <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
    );

    return (
        <div className="relative min-h-[60vh] overflow-hidden">
            {/* Header — on tab background */}
            <div className="sticky top-0 z-10 bg-white border-b-2 border-orange-400 shadow-sm">
                <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-5 sm:py-4">
                    <div className="flex items-center gap-2 min-w-0">
                        <h2 className="text-lg sm:text-2xl font-bold text-stone-800 truncate">Manage Menu</h2>
                        <span className="hidden xs:inline text-xs sm:text-sm text-stone-500 flex-shrink-0">{menuItems.length} items</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => {
                                if (formOpen) {
                                    closeMenuForm();
                                } else {
                                    setShowForm(true);
                                    setEditingItem(null);
                                    setErrorMessage('');
                                    setSuccessMessage('');
                                }
                            }}
                            className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 sm:px-4 rounded-xl font-semibold text-xs sm:text-sm min-h-[44px] transition-all ${formOpen ? 'bg-orange-100 text-orange-700' : 'bg-orange-500 text-white hover:bg-orange-600 shadow-md active:scale-[0.98]'}`}
                        >
                            <span className="text-base sm:text-lg">{formOpen ? '✕' : '+'}</span>
                            <span className="hidden xs:inline">{formOpen ? 'Close' : 'Add'}</span>
                        </button>
                        {!isFullscreen && (
                        <button
                            onClick={() => navigate('/restaurant/menu-fullscreen')}
                            className="inline-flex items-center justify-center p-2.5 sm:px-4 sm:py-2.5 rounded-xl font-semibold text-sm bg-stone-100 text-stone-700 hover:bg-stone-200 active:scale-[0.98] min-h-[44px] min-w-[44px]"
                            title="Fullscreen"
                        >
                            <span className="sm:hidden">⛶</span>
                            <span className="hidden sm:inline">Fullscreen</span>
                        </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:min-h-[calc(100vh-16rem)]">
                {/* Sidebar — warm neutral, orange when selected */}
                <aside className="hidden lg:block w-56 xl:w-64 flex-shrink-0 border-r border-stone-200 bg-white/90">
                    <div className="p-3">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 px-3 py-2">Categories</h3>
                        {categoryOptions.map((opt) => {
                            const count = getCategoryCount(opt.value);
                            const isSelected = opt.value === 'all' ? (filterCategory === 'all' && filterType === 'all') : (filterCategory === opt.value);
                            return (
                                <button
                                    key={opt.value}
                                    onClick={() => { if (opt.value === 'all') { setFilterCategory('all'); setFilterType('all'); } else setFilterCategory(opt.value); }}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all mb-1.5 ${isSelected ? 'bg-orange-500 text-white shadow-md' : 'text-stone-700 hover:bg-orange-50'}`}
                                >
                                    <span className="text-lg">{opt.icon}</span>
                                    <div className="flex-1 min-w-0">
                                        <span className="block font-medium text-sm truncate">{opt.label}</span>
                                        <span className={`block text-xs ${isSelected ? 'text-white/90' : 'text-stone-500'}`}>{count} items</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* Main content */}
                <div className="flex-1 flex flex-col min-w-0 p-3 sm:p-5">
                    {/* Search */}
                    <div className="mb-3 sm:mb-4">
                        <div className="relative group">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 text-lg group-focus-within:text-orange-500 transition-colors">🔍</span>
                            <input
                                type="text"
                                placeholder="Search dishes..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 sm:py-2.5 bg-white border border-stone-200 rounded-2xl text-base sm:text-sm focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400 text-stone-800 placeholder:text-stone-400 transition-all shadow-sm"
                            />
                        </div>
                    </div>
                    {/* Mobile: category strip */}
                    <div className="lg:hidden mb-3 -mx-3 px-3">
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                            <button
                                onClick={() => { setFilterType('all'); setFilterCategory('all'); }}
                                className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-semibold transition-all min-h-[48px] ${filterType === 'all' && filterCategory === 'all' ? 'bg-orange-500 text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200 active:bg-orange-50'}`}
                            >
                                <span>🍽️</span>
                                <span>All</span>
                                <span className={`text-xs ${filterType === 'all' && filterCategory === 'all' ? 'text-white/90' : 'text-stone-500'}`}>({menuItems.length})</span>
                            </button>
                            <button
                                onClick={() => setFilterType('veg')}
                                className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-semibold min-h-[48px] transition-all ${filterType === 'veg' ? 'bg-emerald-500 text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200 border-emerald-200'}`}
                            >
                                <span className="w-2.5 h-2.5 rounded-full bg-current opacity-90" />
                                <span>Veg</span>
                                <span className="text-xs opacity-80">({menuItems.filter(i => i.itemType === 'veg').length})</span>
                            </button>
                            <button
                                onClick={() => setFilterType('nonveg')}
                                className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-semibold min-h-[48px] transition-all ${filterType === 'nonveg' ? 'bg-rose-500 text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200 border-rose-200'}`}
                            >
                                <span className="w-2.5 h-2.5 rounded-full bg-current opacity-90" />
                                <span>Non-Veg</span>
                                <span className="text-xs opacity-80">({menuItems.filter(i => i.itemType === 'nonveg').length})</span>
                            </button>
                            {categoryOptions.slice(1, 12).map((opt) => {
                                const count = getCategoryCount(opt.value);
                                const isSelected = filterCategory === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => setFilterCategory(opt.value)}
                                        className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-semibold min-h-[48px] transition-all ${isSelected ? 'bg-orange-500 text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200 active:bg-orange-50'}`}
                                    >
                                        <span>{opt.icon}</span>
                                        <span className="whitespace-nowrap">{opt.label}</span>
                                        <span className={`text-xs ${isSelected ? 'text-white/90' : 'text-stone-500'}`}>({count})</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {(searchQuery || filterCategory !== 'all' || filterType !== 'all') && (
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm text-stone-600 font-medium">Showing {filteredMenuItems.length} of {menuItems.length}</p>
                            <button onClick={() => { setSearchQuery(''); setFilterCategory('all'); setFilterType('all'); }} className="text-sm font-semibold text-orange-600 hover:text-orange-700 py-2">Clear</button>
                        </div>
                    )}

            {/* Add/Update Form */}
            {(showForm || editingItem) && (
                <div className="bg-white border border-[#e8e8e8] rounded-2xl p-4 sm:p-6 mb-6 shadow-[0_2px_8px_rgba(0,0,0,0.06)] animate-fade-in-up">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                    {editingItem ? 'Update Menu Item' : 'Add New Menu Item'}
                </h3>
                
                {/* Success/Error Messages */}
                {successMessage && (
                    <div className="mb-4">
                        <AlertMessage
                            message={successMessage}
                            type="success"
                            onDismiss={() => setSuccessMessage('')}
                            animate={false}
                        />
                    </div>
                )}
                {errorMessage && (
                    <div className="mb-4">
                        <AlertMessage
                            message={errorMessage}
                            type="error"
                            onDismiss={() => setErrorMessage('')}
                            animate={true}
                        />
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            🏷️ Item Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="itemName"
                            placeholder="Enter item name"
                            value={newItem.itemName}
                            onChange={handleChange}
                            className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 transition-all"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            💰 Price (Rs) <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            name="itemPrice"
                            placeholder="Enter price"
                            value={newItem.itemPrice}
                            onChange={handleChange}
                            className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 transition-all"
                            min="0"
                            step="0.01"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            🌱 Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="itemType"
                            value={newItem.itemType}
                            onChange={handleChange}
                            className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 transition-all"
                            required
                        >
                            <option value="veg">🌱 Veg</option>
                            <option value="nonveg">🍖 Non-Veg</option>
                        </select>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            🏗️ Category <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="itemCategory"
                            value={newItem.itemCategory}
                            onChange={handleChange}
                            className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 transition-all"
                            required
                        >
                            <option value="starters">🍢 Starters</option>
                            <option value="soups">🍜 Soups</option>
                            <option value="breads">🍞 Breads</option>
                            <option value="rice">🍚 Rice</option>
                            <option value="biryani">🍛 Biryani</option>
                            <option value="mainCourse">🍽️ Main Course</option>
                            <option value="curries">🍲 Curries</option>
                            <option value="tandoor">🔥 Tandoor</option>
                            <option value="chinese">🥡 Chinese</option>
                            <option value="beverages">🥤 Beverages</option>
                            <option value="dessert">🍰 Dessert</option>
                            <option value="todaysSpecial">⭐ Today's Special</option>
                            <option value="breakfast">🍳 Breakfast</option>
                            <option value="lunch">🍱 Lunch</option>
                            <option value="meal">🍽️ Meal</option>
                        </select>
                    </div>
                    
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            🖼️ Image <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                type="text"
                                name="itemImage"
                                placeholder="Enter image URL or upload from gallery"
                                value={newItem.itemImage}
                                onChange={handleChange}
                                className="flex-1 p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 transition-all"
                                required
                            />
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                                id="image-upload-input"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingImage}
                                className={`px-4 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${uploadingImage ? 'bg-slate-300 text-white cursor-not-allowed' : 'bg-sky-500 hover:bg-sky-600 text-white shadow-sm'}`}
                            >
                                {uploadingImage ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Uploading...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>📷</span>
                                        <span>Upload from Gallery</span>
                                    </>
                                )}
                            </button>
                        </div>
                        {newItem.itemImage && (
                            <div className="mt-2">
                                <div className="relative inline-block">
                                    <img
                                        src={newItem.itemImage}
                                        alt="Preview"
                                        className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                                        onError={(e) => {
                                            // If image fails to load, show placeholder
                                            e.target.onerror = null;
                                            e.target.src = 'https://via.placeholder.com/128/f3f4f6/9ca3af?text=Image+Error';
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleDeleteImage}
                                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition-all duration-200 transform hover:scale-110 z-10"
                                        title="Remove image"
                                        aria-label="Delete image"
                                    >
                                        ×
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {newItem.itemImage.startsWith('data:image') 
                                        ? 'Image preview (compressed)' 
                                        : 'Image preview'}
                                </p>
                            </div>
                        )}
                    </div>
                    
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">📝 Description</label>
                        <textarea
                            name="itemDescription"
                            placeholder="Enter item description (optional)"
                            value={newItem.itemDescription}
                            onChange={handleChange}
                            rows={3}
                            className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 transition-all resize-none text-slate-800"
                        />
                    </div>
                </div>

                {/* Suggested Items */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">🔗 Suggested Items</label>
                    <AutoSuggestInput
                        allItems={allMenuItems}
                        onSelectionChange={handleSuggestedItemsChange}
                        initialSelected={initialSuggestedItem}
                    />
                </div>

                <button
                    onClick={editingItem ? handleUpdateItem : handleAddItem}
                    className={`${editingItem ? 'bg-stone-600 hover:bg-stone-700 shadow-md' : 'bg-orange-500 hover:bg-orange-600 shadow-md'} text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2`}
                >
                    <span>{editingItem ? '✓' : '+'}</span>
                    {editingItem ? 'Update Item' : 'Add Item'}
                </button>
            </div>
            )}

            {/* Menu Items — colorful cards */}
            <div className="flex-1">
                {filteredMenuItems.length === 0 ? (
                    <div className="text-center py-16 rounded-3xl bg-white border-2 border-dashed border-orange-200 shadow-inner">
                        <p className="text-6xl mb-4">🍽️</p>
                        <h4 className="text-lg font-bold text-stone-800 mb-2">{menuItems.length === 0 ? 'No dishes yet' : 'No matches'}</h4>
                        <p className="text-stone-600 text-sm mb-5">{menuItems.length === 0 ? 'Add your first item to get started' : 'Try different search or filters'}</p>
                        {menuItems.length === 0 && (
                            <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-2xl font-bold text-sm shadow-md transition-all active:scale-[0.98]">
                                + Add Item
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Mobile: redesigned list cards — compact */}
                        <div className="sm:hidden flex flex-col gap-2">
                            {filteredMenuItems.map((item) => (
                                <div key={item._id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-stone-200 border-l-2 border-l-orange-400 hover:shadow-md transition-all flex min-h-[72px]">
                                    <div className="relative w-20 flex-shrink-0 bg-stone-100">
                                        <img
                                            src={item.itemImage || 'https://via.placeholder.com/200/f5f5f4/78716c?text=No+image'}
                                            alt={item.itemName}
                                            className="w-full h-full min-h-[72px] object-cover"
                                            onError={(e) => { if (e.target.src !== 'https://via.placeholder.com/200/f5f5f4/78716c?text=No+image') e.target.src = 'https://via.placeholder.com/200/f5f5f4/78716c?text=No+image'; }}
                                        />
                                        <div className={`absolute top-1 left-1 w-4 h-4 rounded-full border border-white flex items-center justify-center text-[7px] font-bold shadow ${item.itemType === 'veg' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>{item.itemType === 'veg' ? 'V' : 'N'}</div>
                                        {!item.isAvailable && <div className="absolute inset-0 bg-stone-900/50 flex items-center justify-center"><span className="bg-stone-700 text-white text-[8px] font-semibold px-1.5 py-0.5 rounded">Out</span></div>}
                                    </div>
                                    <div className="flex-1 min-w-0 p-2 flex flex-col">
                                        <div className="flex items-start justify-between gap-1 mb-0.5">
                                            <h4 className="font-bold text-stone-800 text-xs leading-tight line-clamp-2">{item.itemName}</h4>
                                            {item.quickServe && <span className="flex-shrink-0 text-[8px] font-medium text-orange-600 bg-orange-100 px-1 py-0.5 rounded">⚡</span>}
                                        </div>
                                        <p className="font-semibold text-orange-600 text-xs mb-0.5">₹{item.itemPrice}</p>
                                        {((item.itemDescription || item.description || '').trim()) ? (
                                            <p className="text-stone-500 text-[10px] leading-snug line-clamp-2 mb-2 min-h-0">{(item.itemDescription || item.description || '').trim()}</p>
                                        ) : null}
                                        <div className="flex gap-1.5 mt-auto">
                                            <button onClick={() => handleToggleAvailability(item._id)} className={`flex-1 min-w-0 py-1.5 rounded-md text-[9px] font-bold ${item.isAvailable ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-stone-200 text-stone-600'}`}>{item.isAvailable ? 'Available' : 'Not available'}</button>
                                            <button
                                                type="button"
                                                disabled={!item.isAvailable}
                                                onClick={() => item.isAvailable && handleToggelQuickServe(item._id)}
                                                className={`p-1.5 rounded-md text-[10px] border disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    !item.isAvailable
                                                        ? 'bg-stone-100 text-stone-400 border-stone-200'
                                                        : item.quickServe
                                                            ? 'bg-orange-500 text-white border-orange-500'
                                                            : 'bg-stone-100 text-stone-500 border-stone-200'
                                                }`}
                                                title={item.isAvailable ? 'Quick serve' : 'Mark item available to change quick serve'}
                                            >
                                                ⚡
                                            </button>
                                            <button onClick={() => { handleEditItem(item); setShowForm(true); }} className="p-1.5 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 inline-flex items-center justify-center" title="Edit" aria-label="Edit"><EditIcon /></button>
                                            <button onClick={() => deleteMenuItem(item._id)} className="p-1.5 rounded-md bg-rose-100 text-rose-600 hover:bg-rose-200 inline-flex items-center justify-center" title="Delete" aria-label="Delete"><DeleteIcon /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Desktop: redesigned grid cards */}
                        <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {filteredMenuItems.map((item) => (
                                <div
                                    key={item._id}
                                    className="bg-white rounded-2xl overflow-hidden border border-stone-200 shadow-md hover:shadow-xl hover:border-orange-300 transition-all duration-200 flex flex-col border-t-4 border-t-orange-400"
                                >
                                    <div className="relative aspect-[4/3] w-full bg-stone-100 overflow-hidden">
                                        <img
                                            src={item.itemImage || 'https://via.placeholder.com/300/f5f5f4/78716c?text=No+image'}
                                            alt={item.itemName}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { if (e.target.src !== 'https://via.placeholder.com/300/f5f5f4/78716c?text=No+image') e.target.src = 'https://via.placeholder.com/300/f5f5f4/78716c?text=No+image'; }}
                                        />
                                        <div className={`absolute top-2 left-2 w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold shadow ${item.itemType === 'veg' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>{item.itemType === 'veg' ? 'V' : 'N'}</div>
                                        {!item.isAvailable && <div className="absolute inset-0 bg-stone-900/50 flex items-center justify-center"><span className="bg-stone-700 text-white text-xs font-semibold px-2 py-1 rounded-lg">Sold out</span></div>}
                                    </div>
                                    <div className="p-3 flex-1 flex flex-col min-w-0">
                                        <div className="flex items-start justify-between gap-1 mb-1">
                                            <h4 className="font-bold text-stone-800 text-sm leading-tight line-clamp-2 flex-1 min-w-0" title={item.itemName}>{item.itemName}</h4>
                                            {item.quickServe && <span className="flex-shrink-0 text-[9px] font-semibold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">⚡</span>}
                                        </div>
                                        {item.itemDescription && <p className="text-stone-500 text-xs leading-snug mb-2 line-clamp-2">{item.itemDescription}</p>}
                                        <p className="font-bold text-orange-600 text-sm mb-3">₹{item.itemPrice}</p>
                                        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-stone-100 mt-auto">
                                            <button onClick={() => handleToggleAvailability(item._id)} className={`flex-1 min-w-[60px] py-1.5 rounded-lg text-[10px] font-bold ${item.isAvailable ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-stone-200 text-stone-600'}`}>{item.isAvailable ? 'Available' : 'Not available'}</button>
                                            <button
                                                type="button"
                                                disabled={!item.isAvailable}
                                                onClick={() => item.isAvailable && handleToggelQuickServe(item._id)}
                                                className={`py-1.5 px-2 rounded-lg text-[10px] font-bold border disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    !item.isAvailable
                                                        ? 'bg-stone-100 text-stone-400 border-stone-200'
                                                        : item.quickServe
                                                            ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600'
                                                            : 'bg-stone-100 text-stone-500 border-stone-200'
                                                }`}
                                                title={item.isAvailable ? 'Quick serve' : 'Mark item available to change quick serve'}
                                            >
                                                ⚡
                                            </button>
                                            <button onClick={() => { handleEditItem(item); setShowForm(true); }} className="py-1.5 px-2 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 inline-flex items-center justify-center" title="Edit"><EditIcon /></button>
                                            <button onClick={() => deleteMenuItem(item._id)} className="py-1.5 px-2 rounded-lg bg-rose-100 text-rose-600 hover:bg-rose-200 inline-flex items-center justify-center" title="Delete"><DeleteIcon /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
                </div>
            </div>
            <style>{`
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.6s ease-out;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
            `}</style>
        </div>
    );
};

export default MenuSection;
